import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Zap, Trophy, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const Index = () => {
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);

  const levels = [
    {
      id: 'beginner',
      title: 'Beginner',
      description: 'Perfect for warming up your brain',
      icon: Brain,
      color: 'from-teal-500 to-blue-500',
      questions: 10,
      duration: '10 minutes',
    },
    {
      id: 'intermediate',
      title: 'Intermediate',
      description: 'Challenge yourself with moderate difficulty',
      icon: Zap,
      color: 'from-purple-500 to-blue-500',
      questions: 15,
      duration: '15 minutes',
    },
    {
      id: 'advanced',
      title: 'Advanced',
      description: 'Test the limits of your intelligence',
      icon: Trophy,
      color: 'from-orange-500 to-pink-500',
      questions: 20,
      duration: '20 minutes',
    },
  ];

  const handleLevelSelect = (levelId: string) => {
    setSelectedLevel(levelId);
    setShowDisclaimer(true);
  };

  const handleStartTest = () => {
    if (acceptedDisclaimer) {
      navigate(`/test/${selectedLevel}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-purple-100 sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <div className="w-12 h-12 rounded-xl gradient-purple-blue flex items-center justify-center">
                <Brain className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">IQ Test Pro</h1>
                <p className="text-xs text-gray-600">Discover Your Potential</p>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Test Your Intelligence
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Take our scientifically designed IQ test and discover where you stand among the global population
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-teal-500"></div>
              <span className="text-gray-600">Quick & Accurate</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
              <span className="text-gray-600">Global Comparison</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-gray-600">PDF Certificate</span>
            </div>
          </div>
        </motion.div>

        {/* Level Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {levels.map((level, index) => {
            const Icon = level.icon;
            return (
              <motion.div
                key={level.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="relative overflow-hidden group hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 hover:border-purple-300"
                  onClick={() => handleLevelSelect(level.id)}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${level.color} opacity-5 group-hover:opacity-10 transition-opacity`}></div>
                  
                  <div className="p-8 relative">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${level.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={32} />
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-3 text-gray-900">{level.title}</h3>
                    <p className="text-gray-600 mb-6">{level.description}</p>
                    
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Questions:</span>
                        <span className="font-semibold text-gray-900">{level.questions}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-semibold text-gray-900">{level.duration}</span>
                      </div>
                    </div>
                    
                    <Button 
                      className={`w-full bg-gradient-to-r ${level.color} hover:opacity-90 transition-opacity group-hover:scale-105 transform duration-200`}
                      size="lg"
                    >
                      Start Test
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-20 bg-white rounded-3xl shadow-lg p-8 md:p-12 max-w-4xl mx-auto"
        >
          <h3 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">What You'll Get</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Detailed IQ Score', desc: 'Get your precise IQ score with percentile ranking' },
              { title: 'Global Comparison', desc: 'See how you rank against people worldwide' },
              { title: 'Downloadable PDF', desc: 'Save and share your results as a certificate' },
              { title: 'Instant Results', desc: 'Get your results immediately after completion' },
            ].map((feature, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="w-10 h-10 rounded-lg gradient-purple-blue flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">{idx + 1}</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{feature.title}</h4>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-lg border-t border-purple-100 mt-20">
        <div className="container mx-auto px-6 py-8 text-center text-gray-600">
          <p className="text-sm">
            © 2024 IQ Test Pro. For entertainment and educational purposes only.
          </p>
        </div>
      </footer>

      {/* Disclaimer Dialog */}
      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <AlertCircle className="text-orange-500" size={28} />
              Important Disclaimer
            </DialogTitle>
            <DialogDescription className="text-base text-left space-y-4 mt-4">
              <p>
                <strong>Please read carefully before proceeding:</strong>
              </p>
              <ul className="space-y-2 list-disc list-inside">
                <li>This IQ test is designed for entertainment and educational purposes only.</li>
                <li>Results should not be used for professional assessment, diagnosis, or employment decisions.</li>
                <li>IQ tests are influenced by various factors including education, culture, and test-taking experience.</li>
                <li>Your score represents performance on this specific test and may not reflect your full cognitive abilities.</li>
                <li>For professional evaluation, please consult a licensed psychologist.</li>
                <li>We do not store personal data. Results are generated client-side only.</li>
                <li>The test should be taken in a quiet environment without interruptions for best results.</li>
              </ul>
              
              <div className="flex items-start gap-3 mt-6 p-4 bg-purple-50 rounded-lg">
                <Checkbox 
                  id="disclaimer"
                  checked={acceptedDisclaimer}
                  onCheckedChange={(checked) => setAcceptedDisclaimer(checked as boolean)}
                />
                <label 
                  htmlFor="disclaimer"
                  className="text-sm font-medium leading-relaxed cursor-pointer"
                >
                  I have read and understood the disclaimer. I agree to take this test for entertainment purposes only and understand that results should not be used for professional decisions.
                </label>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-3 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDisclaimer(false);
                setAcceptedDisclaimer(false);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleStartTest}
              disabled={!acceptedDisclaimer}
              className="flex-1 gradient-purple-blue"
            >
              Start Test
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Index;
